'use client'

// The workshop shelf: the newest prints, in the world.
//
// Sam's cap-everything worry answered in the geometry: the API serves
// the newest few (printShelfSize sim-side), each print arrives baked to
// a one-unit bounding box and is shown small, so the whole shelf holds
// to roughly one building's worth of triangles no matter how long the
// printer runs. Older prints keep their record and file; they just
// leave the scene, boxed up.
//
// Fetch once on mount, the art wall's pattern: prints arrive slower
// than once a day, and a poll would mostly be asking the same question
// of a machine that answered it. New prints appear for a viewer on
// their next visit, which is how shelves work.
//
// The line stands in front of the workshop's frontage, spaced along
// the pavement side. SLOTS is deliberately a plain array so Sam can
// nudge positions without reading the rest of the file.

import { Component, Suspense, useEffect, useState } from 'react'
import * as THREE from 'three/webgpu'
import { useModel } from '../hooks/useModel'
import { toNode } from '../hooks/useSceneNodeMaterials'
import { ditherMaterial } from '../effects/ditherRegistry'

// On the pavement outside the 3dPrinters building's west face. The
// walls span x 108.1-121.6, z 9.0-21.8 (measured from the glb, not
// eyeballed - the first slot layout sat INSIDE them), and the entrance
// stairs stick out west at z 13.3-15.2, so the line flanks the door:
// newest prints north of the stairs where he walks up, the rest
// filling south. All x <= 107.8, clear of the wall by a print's width.
const SLOTS = [
  [106.6, 0.02, 16.4], [106.9, 0.02, 17.5], [107.2, 0.02, 18.6],
  [107.5, 0.02, 19.7], [106.6, 0.02, 12.4], [107.0, 0.02, 11.3],
  [107.4, 0.02, 10.2], [107.8, 0.02, 20.7], [107.8, 0.02, 9.3],
  [106.3, 0.02, 21.5],
]
const PRINT_SCALE = 0.55

// A missing print must cost the shelf slot, never the world. Same
// boundary the spray walls carry, for the same reason.
class Absent extends Component {
  state = { gone: false }
  static getDerivedStateFromError() { return { gone: true } }
  componentDidCatch(err) { console.warn('[printshelf] a print did not load:', err?.message || err) }
  render() { return this.state.gone ? null : this.props.children }
}

function OnePrint({ id, slot, rev }) {
  // rev rolls the url when a print's file is re-treated server-side:
  // the glb route is immutable-cached, so new bytes need a new url.
  const { scene } = useModel(`/api/prints/${id}${rev ? `?r=${rev}` : ''}`)
  useEffect(() => {
    const converted = new Map()
    scene.traverse((o) => {
      if (!o.isMesh) return
      o.castShadow = true
      o.receiveShadow = true
      // Arrives whenever he prints; compiled before the shelf is looked at.
      o.userData.precompile = true
      o.material = Array.isArray(o.material)
        ? o.material.map((m) => toNode(m, converted))
        : toNode(o.material, converted)
      // Dithered from the first frame, and the pixel look on the map
      // before the sweep would force it.
      const mats = Array.isArray(o.material) ? o.material : [o.material]
      for (const m of mats) {
        if (m.map) { m.map.magFilter = THREE.NearestFilter; m.map.minFilter = THREE.NearestFilter; m.map.needsUpdate = true }
        ditherMaterial(m)
      }
    })
  }, [scene])
  return (
    <primitive
      object={scene}
      position={slot}
      scale={PRINT_SCALE}
      rotation={[0, -Math.PI / 2, 0]}
    />
  )
}

export function PrintShelf() {
  const [prints, setPrints] = useState([])
  useEffect(() => {
    let dead = false
    fetch('/api/prints?limit=10')
      .then((r) => r.json())
      .then((d) => { if (!dead && Array.isArray(d.prints)) setPrints(d.prints) })
      .catch(() => { /* a bare shelf is a true shelf until he prints */ })
    return () => { dead = true }
  }, [])

  return (
    <>
      {prints.slice(0, SLOTS.length).map((p, i) => (
        <Absent key={`${p.id}-${p.rev || 1}`}>
          <Suspense fallback={null}>
            <OnePrint id={p.id} slot={SLOTS[i]} rev={p.rev} />
          </Suspense>
        </Absent>
      ))}
    </>
  )
}

export default PrintShelf
